import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/AppHelp.css';

const faqs = [
  {
    id: 'faq-1',
    category: 'Account & Login',
    question: 'My email verification is not arriving',
    answer: 'Start by checking that you entered your email address correctly when you signed up. The most common cause is a typo, try logging in first. If your email is not recognised you will see a notification on the login screen, which means the account was not created with that address.\n\nIf the address is correct, check your junk or spam folder. The verification email comes from noreply@zellawellbeing.com, add this to your safe senders list if needed.\n\nAlso make sure your app is up to date by checking for updates in the App Store or Google Play.\n\nIf none of the above resolves it, try signing up again with a different email address, or contact us at hello@zellawellbeing.com.',
  },
  {
    id: 'faq-2',
    category: 'Account & Login',
    question: 'I am not receiving the password reset email',
    answer: 'Tap the Forgot Password button on the login screen and enter your email address. The reset link comes from noreply@zellawellbeing.com.\n\nIf it does not arrive, check your junk folder and make sure you are entering the exact email address used to sign up. If the problem continues, contact hello@zellawellbeing.com.',
  },
  {
    id: 'faq-3',
    category: 'Surveys',
    question: 'My survey is not submitting',
    answer: 'There are two common reasons a survey may not record:\n\nFirst, your team may have fewer than 5 members. Zella requires at least 5 people in a team before survey data is collected, this protects everyone\'s anonymity. If your team is small, let your admin know so they can add members or merge teams.\n\nSecond, check for an app update in the App Store or Google Play. An outdated version can occasionally cause submission issues.\n\nIf neither of these applies, contact hello@zellawellbeing.com with a description of what happens when you try to submit.',
  },
  {
    id: 'faq-4',
    category: 'Surveys',
    question: 'How often should I complete surveys?',
    answer: 'This is set by your group or company administrator. As a general guide, completing a survey each working week gives a useful picture of how your team is doing over time. Your admin may ask for a different frequency, follow whatever they advise.\n\nSurveys only take around 30 seconds to complete.',
  },
  {
    id: 'faq-5',
    category: 'Privacy',
    question: 'Can anyone see my individual answers?',
    answer: 'No. Your individual responses are never visible to anyone, not your employer, not your team manager, and not Zella staff.\n\nYour responses are stored against only a team code and a company code. Your name is never attached to a response. The only data your company admin can see is the team average, a combined happiness score and tiredness score across all members of your team for each week. No individual breakdown is ever available.',
  },
];

const privacyPoints = [
  {
    who: 'You',
    can: 'See the number of people in the team you belong to and your survey answers before they are submitted.',
    cannot: 'See individual or aggregated responses.',
  },
  {
    who: 'Your company admin',
    can: 'See team-level average happiness and tiredness scores per week. See which teams exist and how many members each has.',
    cannot: "See any individual's responses. See breakdowns below team level.",
  },
  {
    who: 'Zella',
    can: 'Access individual surveys anonymised at the group level.',
    cannot: 'Any link between any survey response and an individual.',
  },
];

const employeePractice = [
  {
    title: 'Use the app to self-assess your fatigue risk',
    description: 'It is particularly useful if you are feeling tired, or are about to take on a long drive or operate machinery, as well as before and after shift work or during unsociable hours. It cannot make decisions for you, but can empower you to make sensible choices.',
  },
  {
    title: 'Answer honestly',
    description: 'Your individual answers are completely anonymous. Zella works best when responses reflect how you actually feel. Your team benefits most when the data is genuine.',
  },
  {
    title: 'Share your results',
    description: 'The data is anonymous and allows your employer to monitor trends across teams and the whole company, to help direct support and resources if required.',
  },
  {
    title: 'Use the app regularly, even without a specific reason',
    description: 'The more data you provide, the more relevant and useful it is. Every time you use the app you are helping to keep yourself and your colleagues safe.',
  },
  {
    title: 'Keep the app updated',
    description: 'Check for updates regularly in the App Store or Google Play. Updates fix known issues and ensure your surveys submit correctly.',
  },
];

const employerPractice = [
  {
    title: 'Encourage honest reporting',
    description: 'Data will be less useful if employees are second-guessing what you want to see. Make clear that responses are individually anonymous and that there are no wrong answers.',
  },
  {
    title: 'Encourage maximum engagement',
    description: 'The more data you gather, the more useful and representative it will be. Remind your team regularly and reinforce why it matters.',
  },
  {
    title: 'Share data when appropriate',
    description: 'Employees will be more motivated to use the app if they can see that the data is meaningful. Consider sharing team-level trends in team meetings or updates.',
  },
  {
    title: 'Take action when required',
    description: 'Employees will be more likely to engage if they see that their data leads to positive action. When scores flag an issue, follow up visibly and promptly.',
  },
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ah-faq-item ${open ? 'open' : ''}`}>
      <button className="ah-faq-question" onClick={() => setOpen(!open)}>
        <span>{faq.question}</span>
        <span className="ah-faq-chevron">{open ? '\u2212' : '+'}</span>
      </button>
      {open && (
        <div className="ah-faq-answer">
          {faq.answer.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppHelp() {
  const [activeTab, setActiveTab] = useState('faqs');

  const tabs = [
    { id: 'faqs', label: 'FAQs' },
    { id: 'privacy', label: 'Your Privacy' },
    { id: 'bestpractice', label: 'Best Practice' },
  ];

  const faqsByCategory = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="ah-page">

      <div className="ah-topbar">
        <Link to="/" className="ah-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Back to Zella
        </Link>
        <div className="ah-logo-wrapper">
          <img src="/Zella logo green.png" alt="Zella" className="ah-logo-img" />
        </div>
      </div>

      <div className="ah-hero">
        <div className="ah-hero-inner">
          <div className="ah-hero-tag">App Help</div>
          <h1>Help for Zella app users</h1>
          <p>Answers to common questions, your privacy explained, and tips for getting the most out of Zella.</p>
          <a href="mailto:hello@zellawellbeing.com" className="ah-contact-link">
            Still need help? Email hello@zellawellbeing.com
          </a>
        </div>
      </div>

      <div className="ah-body">
        <div className="ah-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`ah-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="ah-content">

          {activeTab === 'faqs' && (
            <div className="ah-panel">
              <div className="ah-panel-header">
                <h2>Frequently asked questions</h2>
                <p>Common issues and how to fix them. If something is not covered here, email us at hello@zellawellbeing.com.</p>
              </div>
              {Object.entries(faqsByCategory).map(([category, items]) => (
                <div className="ah-faq-group" key={category}>
                  <h3 className="ah-faq-category">{category}</h3>
                  <div className="ah-faq-list">
                    {items.map(faq => (
                      <FAQItem key={faq.id} faq={faq} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="ah-panel">
              <div className="ah-panel-header">
                <h2>Your privacy</h2>
                <p>Zella is built around anonymity. Here is exactly what each party can and cannot see.</p>
              </div>
              <div className="ah-privacy-table">
                <div className="ah-privacy-header">
                  <span>Who</span>
                  <span>Can see</span>
                  <span>Cannot see</span>
                </div>
                {privacyPoints.map((row, i) => (
                  <div className="ah-privacy-row" key={i}>
                    <span className="ah-privacy-who">{row.who}</span>
                    <span className="ah-privacy-can">
                      <span className="ah-tick">&#10003;</span>
                      {row.can}
                    </span>
                    <span className="ah-privacy-cannot">
                      <span className="ah-cross">&#10007;</span>
                      {row.cannot}
                    </span>
                  </div>
                ))}
              </div>
              <div className="ah-callout">
                <div className="ah-callout-bar" />
                <div>
                  <strong>The core principle</strong>
                  <p>Your survey responses are stored against a team code and a company code only. Your name is never attached to any response. Even if someone could access the raw database, they could not identify which answers belong to you. The requirement for teams of 5 or more exists specifically to reinforce this protection.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bestpractice' && (
            <div className="ah-panel">
              <div className="ah-panel-header">
                <h2>Getting the most out of Zella</h2>
                <p>Advice for employees and employers on how to make the data as useful as possible.</p>
              </div>

              <h3 className="ah-practice-section-label">For employees</h3>
              <div className="ah-practice-list">
                {employeePractice.map((item, i) => (
                  <div className="ah-practice-item" key={i}>
                    <div className="ah-practice-num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="ah-practice-body">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="ah-practice-section-label" style={{ marginTop: '2rem' }}>For employers</h3>
              <div className="ah-practice-list">
                {employerPractice.map((item, i) => (
                  <div className="ah-practice-item" key={i}>
                    <div className="ah-practice-num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="ah-practice-body">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ah-callout">
                <div className="ah-callout-bar" />
                <div>
                  <strong>Remember</strong>
                  <p>Zella works best as a consistent habit. The more regularly your team submits surveys, the more clearly trends appear, which means employers can act earlier when something needs attention.</p>
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="ah-footer-contact">
          <p>Could not find what you were looking for?</p>
          <a href="mailto:hello@zellawellbeing.com">hello@zellawellbeing.com</a>
        </div>
      </div>

    </div>
  );
}
